import { segmentDistanceSq } from "./geo/distance";
import { metresPerDegree } from "./metres";
// Zone-label ANCHORS that never sit on a zone's edge: a symbol layer bound to a polygon source lets
// MapLibre place the text at the polygon's centroid-ish point — which lands ON or OUTSIDE the
// boundary for concave/annular zones (a U-shape's centroid is in the gap). Every zone-label surface
// instead renders from an explicit POINT at the polygon's **pole of inaccessibility** — the
// interior point farthest from every boundary (Mapbox's polylabel algorithm: quadtree cell
// refinement over a signed distance-to-outline function), computed in local meters so elongated
// fields don't skew.
type Pt = [number, number];
type Ring = Pt[];

/* * signed distance from (x,y) to the polygon outline: + inside, − outside (even-odd over ALL rings) */
function signedDist(x: number, y: number, rings: Ring[]): number {
  let inside = false;
  let min2 = Infinity;
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]!;
      const [xj, yj] = ring[j]!;
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
        inside = !inside;
      min2 = Math.min(min2, segmentDistanceSq(x, y, xi, yi, xj, yj));
    }
  }
  return (inside ? 1 : -1) * Math.sqrt(min2);
}

/*
 *
 * Pole of inaccessibility of ONE polygon (outer ring + holes), coordinates in METERS.
 * Quadtree refinement to `precision` meters; deterministic (fixed traversal, no RNG).
 */
function polylabelM(rings: Ring[], precision = 2): Pt {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const [x, y] of rings[0] ?? []) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const width = maxX - minX,
    height = maxY - minY;
  const cellSize = Math.min(width, height);
  if (!(cellSize > 0)) return [minX, minY];
  interface Cell {
    x: number;
    y: number;
    h: number;
    d: number;
    max: number;
  }
  const mk = (x: number, y: number, h: number): Cell => {
    const d = signedDist(x, y, rings);
    return { x, y, h, d, max: d + h * Math.SQRT2 };
  };
  // seed: a coarse grid + the bbox centroid (the classic polylabel seeds)
  const queue: Cell[] = [];
  const h0 = cellSize / 2;
  for (let x = minX; x < maxX; x += cellSize)
    for (let y = minY; y < maxY; y += cellSize)
      queue.push(mk(x + h0, y + h0, h0));
  let best = mk(minX + width / 2, minY + height / 2, 0);
  let guard = 0;
  while (queue.length && guard++ < 4000) {
    // max-scan (label polygons are small; a heap would be noise)
    let bi = 0;
    for (let i = 1; i < queue.length; i++)
      if (queue[i]!.max > queue[bi]!.max) bi = i;
    const c = queue.splice(bi, 1)[0]!;
    if (c.d > best.d) best = c;
    if (c.max - best.d <= precision) continue;
    const h = c.h / 2;
    queue.push(
      mk(c.x - h, c.y - h, h),
      mk(c.x + h, c.y - h, h),
      mk(c.x - h, c.y + h, h),
      mk(c.x + h, c.y + h, h),
    );
  }
  return [best.x, best.y];
}

const shoelace = (r: Ring): number => {
  let a = 0;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++)
    a += r[j]![0] * r[i]![1] - r[i]![0] * r[j]![1];
  return Math.abs(a / 2);
};

/* * the label anchor (lng/lat) for a Polygon/MultiPolygon: polylabel of the LARGEST part, holes honored */
export function zoneLabelAnchor(
  geom: GeoJSON.Geometry,
): [number, number] | null {
  const parts: Ring[][] =
    geom.type === "Polygon"
      ? [geom.coordinates as unknown as Ring[]]
      : geom.type === "MultiPolygon"
        ? (geom.coordinates as unknown as Ring[][])
        : [];
  let bestPart: Ring[] | null = null;
  let bestArea = -1;
  for (const p of parts) {
    const a = shoelace(p[0] ?? []);
    if (a > bestArea) {
      bestArea = a;
      bestPart = p;
    }
  }
  if (!bestPart || !bestPart[0]?.length) return null;
  const lat0 = bestPart[0][0]![1];
  const { kx, ky } = metresPerDegree(lat0);
  const m = bestPart.map((ring) =>
    ring.map(([lng, lat]) => [lng * kx, lat * ky] as Pt),
  );
  const [x, y] = polylabelM(m);
  return [x / kx, y / ky];
}

/*
 *
 * Append one POINT feature per polygon feature (same properties) at its pole of inaccessibility.
 * Zone layers use the result as their single source: fill/line render the polygons, the label symbol
 * layer filters to `$type Point` — so the text always sits deep inside the zone instead of wherever
 * MapLibre's polygon placement falls.
 */
export function withZoneLabelPoints(
  fc: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  if (fc?.type !== "FeatureCollection") return fc;
  const pts: GeoJSON.Feature[] = [];
  for (const f of fc.features) {
    if (f.geometry?.type !== "Polygon" && f.geometry?.type !== "MultiPolygon")
      continue;
    const a = zoneLabelAnchor(f.geometry);
    if (a)
      pts.push({
        type: "Feature",
        properties: { ...(f.properties ?? {}) },
        geometry: { type: "Point", coordinates: a },
      });
  }
  return { ...fc, features: [...fc.features, ...pts] };
}
